/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IAny, IComponent, IComponentProps, ICoreContext, IGeneric, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Button, ITelemetryContent, Collapse } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import classnames from 'classnames';

import { IRefinerItemDateRangeProps, RefinerItemDateRangeComponent } from './refiner-item-daterange.component';
import { IRefinerItemCheckProps, RefinerItemCheckComponent } from './refiner-item-check.component';
import {
    IRefinerItemData,
    IRefinerItemToggleNotification,
    IRefinerOptionsData,
    RefinerItemType,
    RefinerOptionsType,
    isMatchingRefinementCriterion
} from './common';

// eslint-disable-next-line @typescript-eslint/naming-convention -- This is the standard rule for naming a constant.
const DEFAULT_REFINER_OPTIONS_EXPAND_TIMEOUT = 350;

/**
 * Class names used in component.
 */
const componentClassName = 'msc-refiner-options';
const expandedClassName = `${componentClassName}__toggle_expanded`;
const collapsedClassName = `${componentClassName}__toggle_collapsed`;
const listClassName = `${componentClassName}__list`;
const listItemClassName = `${componentClassName}__list-item`;

/**
 * IRefinerOptionsComponent interface.
 */
interface IRefinerOptionsComponent extends IComponent<IRefinerOptionsProps> {}

/**
 * IRefinerOptionsProps interface.
 */
export interface IRefinerOptionsProps extends IComponentProps<IRefinerOptionsData> {
    context: ICoreContext<IGeneric<IAny>>;
    className: string;
    isDisabled: boolean;
    isExpandedOnInitialLoad: boolean;
    expandTimeout?: number;
    telemetryContent?: ITelemetryContent;
    onUpdateRefiners(notification: IRefinerItemToggleNotification): void;
}

/**
 * RefinerOptions component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerOptionsFunctionalComponent: React.FC<IRefinerOptionsProps> = (props: IRefinerOptionsProps) => {
    const {
        className,
        context,
        id: moduleId,
        typeName,
        data,
        isDisabled,
        isExpandedOnInitialLoad,
        expandTimeout,
        telemetryContent,
        onUpdateRefiners
    } = props;

    const [shouldRefinerExpand, setShouldRefinerExpand] = React.useState(isExpandedOnInitialLoad);
    const [isRefinerExpanded, setIsRefinerExpanded] = React.useState(false);

    const onEntering = React.useCallback(() => {
        setIsRefinerExpanded(!isRefinerExpanded);
    }, [isRefinerExpanded]);

    const onToggleRefinerOptions = React.useCallback(() => {
        setShouldRefinerExpand(!shouldRefinerExpand);
    }, [shouldRefinerExpand]);

    const onClickRefinerItem = React.useCallback(
        (selectedRefinerItem: IRefinerItemData) => () => {
            onUpdateRefiners({
                refinerOptionsData: data,
                refinerItemData: selectedRefinerItem,
                isSelecting: !selectedRefinerItem.isChecked
            });
        },
        [onUpdateRefiners, data]
    );

    const onMinValueUpdate = React.useCallback(
        (selectedRefinerItem: IRefinerItemData) => (updatedMin: string) => {
            onUpdateRefiners({
                refinerOptionsData: data,
                refinerItemData: selectedRefinerItem,
                isSelecting: true,
                rangeStart: updatedMin
            });
        },
        [onUpdateRefiners, data]
    );

    const onMaxValueUpdate = React.useCallback(
        (selectedRefinerItem: IRefinerItemData) => (updatedMax: string) => {
            onUpdateRefiners({
                refinerOptionsData: data,
                refinerItemData: selectedRefinerItem,
                isSelecting: true,
                rangeEnd: updatedMax
            });
        },
        [onUpdateRefiners, data]
    );

    /**
     * Renders RefinerItem components.
     * @param refineItems - Contains the refiner items.
     * @returns - Refiner item components.
     */
    const renderRefinerItems = (refinerItems: IRefinerItemData[]) => {
        return refinerItems.map((refinerItem, index) => {
            const isSingleSelectValue = data.typeValue === RefinerOptionsType.SingleSelect;
            const selectedItem = data.selectedRefinerItems.find((selectedItem: IRefinerItemData) =>
                isMatchingRefinementCriterion(refinerItem, selectedItem)
            );

            refinerItem.isChecked = !!selectedItem;
            refinerItem.minValue = selectedItem?.minValue;
            refinerItem.maxValue = selectedItem?.maxValue;

            const refinerItemCheckProps = {
                context: context,
                id: moduleId,
                typeName: typeName,
                data: refinerItem,
                label: refinerItem.label ?? '',
                ariaLabel: refinerItem.label ?? '',
                isChecked: refinerItem.isChecked,
                isSingleSelect: isSingleSelectValue,
                isDisabled: isDisabled,
                telemetryContent: telemetryContent,
                onClick: onClickRefinerItem(refinerItem)
            } as IRefinerItemCheckProps;

            switch (refinerItem.itemType) {
                case RefinerItemType.DateRange:
                    const refinerDataRangeProps = {
                        context: context,
                        id: moduleId,
                        typeName: typeName,
                        data: refinerItem,
                        minInputLabel: refinerItem.minLabel ?? '',
                        maxInputLabel: refinerItem.maxLabel ?? '',
                        minInputAriaLabel: refinerItem.minLabel ?? '',
                        maxInputAriaLabel: refinerItem.maxLabel ?? '',
                        minRangeValue: refinerItem.minValue,
                        maxRangeValue: refinerItem.maxValue,
                        isDisabled: isDisabled,
                        onBlurMinInput: onMinValueUpdate(refinerItem),
                        onBlurMaxInput: onMaxValueUpdate(refinerItem)
                    } as IRefinerItemDateRangeProps;

                    return (
                        <li className={listItemClassName} id={`${refinerItem.label}_${index}`} key={refinerItem.refinerItemId}>
                            <RefinerItemCheckComponent {...refinerItemCheckProps} />
                            {refinerItem.isChecked && <RefinerItemDateRangeComponent {...refinerDataRangeProps} />}
                        </li>
                    );
                default:
                    return (
                        <li className={listItemClassName} id={`${refinerItem.label}_${index}`} key={refinerItem.refinerItemId}>
                            <RefinerItemCheckComponent {...refinerItemCheckProps} />
                        </li>
                    );
            }
        });
    };

    return (
        <div className={classnames(className, componentClassName, 'list-group', data.name ?? '')} key={data.name}>
            <Button
                className={shouldRefinerExpand ? expandedClassName : collapsedClassName}
                aria-label={data.name}
                aria-expanded={shouldRefinerExpand}
                onClick={onToggleRefinerOptions}
            >
                {data.name}
            </Button>
            <Collapse
                isOpen={shouldRefinerExpand}
                onEntering={onEntering}
                timeout={expandTimeout ?? DEFAULT_REFINER_OPTIONS_EXPAND_TIMEOUT}
            >
                <ul className={classnames(listClassName, data.name ?? '')} aria-label={data.name}>
                    {renderRefinerItems(data.refinerItems)}
                </ul>
            </Collapse>
        </div>
    );
};

/**
 * RefinerOptions component.
 */
export const RefinerOptionsComponent: React.FunctionComponent<IRefinerOptionsProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IRefinerOptionsComponent
>('Refiner-Options', {
    component: React.memo(RefinerOptionsFunctionalComponent)
});
